// Deps
import { createComponent } from "react-fela";
import { gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { textStyles, themeText } from "~/styleguide/typography";
import get from "lodash.get";
import moduleData from "~/data/module-data.json";

// Context
import { useContext } from "react";
import { getThemeForModule } from "~/context/theme";
import { DataContext } from "~/context/content";

// Components
import WordpressLink, {
    getLinkFromRelationship
} from "~/components/global/links";
import { ContentMaxWidth } from "~/components/layout/max-width";

/**
 * Global CTA
 *
 * Can use global content from the Footer Data options page
 * or be customized per-page with a theme and content.
 */
export default function CTA({ block }) {
    let {
        use_global_content,
        theme_colors,
        theme_button_override,
        use_graphic_background,
        background_image,
        heading,
        subheading,
        link_url,
        link_label,
        link_content,
        link_type,
        acf_fc_layout,
        anchor_id,
        link_class
    } = block;

    const pageData = useContext(DataContext);
    const page = get(pageData, "listing_page", pageData);

    const theme = getThemeForModule(theme_colors);
    const textTheme = themeText(theme);

    let hasButtonOverride =
        theme_button_override && theme_button_override !== "no";

    if (get(theme_button_override, [0]) === "no") {
        hasButtonOverride = false;
    }

    const buttonTheme = hasButtonOverride
        ? getThemeForModule(theme_button_override)
        : theme;

    if (use_global_content) {
        // Grab the data from the Page or the Globals
        const contentObject = use_global_content ? moduleData : page;
        const keyPrefix = use_global_content ? "data." : "";

        heading = get(contentObject, `${keyPrefix}acf.footer_cta_heading`);
        subheading = get(
            contentObject,
            `${keyPrefix}acf.footer_cta_subheading`
        );
        link_url = get(contentObject, `${keyPrefix}acf.footer_cta_link`);
        link_content = get(
            contentObject,
            `${keyPrefix}acf.footer_cta_link_content`
        );
        link_type = get(contentObject, `${keyPrefix}acf.footer_cta_link_type`);
        link_label = get(
            contentObject,
            `${keyPrefix}acf.footer_cta_link_label`
        );
    }

    const link = getLinkFromRelationship(link_type, link_content, link_url);

    // Must have some content set to be displayed
    if (!heading && !subheading && !link_url && !link_content) return null;

    let backgroundStyles = {
        background: (theme.gradient ? theme.gradient : theme.primary)
    };

    if (use_graphic_background && !background_image) {
        backgroundStyles.filigree = (theme.filigreeEscaped ? theme.filigreeEscaped : theme.secondaryEscaped);
    }

    if (background_image) {
        backgroundStyles = {
          backgroundImage: "url(" + background_image + ")",
          backgroundSize: "cover",
          backgroundColor: theme.primary
        };
    }

    return (
        <Container
            backgroundColor={theme.primary}
            secondaryColor={theme.secondaryEscaped}
            id={anchor_id}
            className="module-cta"
            extend={backgroundStyles}
        >
            <ContentMaxWidth>
                {/* Heading */}
                {heading && (
                    <Heading
                        type={acf_fc_layout}
                        extend={textTheme}
                        dangerouslySetInnerHTML={{ __html: heading }}
                    />
                )}
                {/* Subheading */}
                {subheading && (
                    <Subheading
                        type={acf_fc_layout}
                        extend={textTheme}
                        dangerouslySetInnerHTML={{ __html: subheading }}
                    />
                )}
                {/* Link */}
                {link.url && link_label && (
                    <WordpressLink
                        layout="button"
                        url={link.url}
                        size="large"
                        label={link_label}
                        backgroundColor={
                            hasButtonOverride ? buttonTheme.primary : null
                        }
                        theme={hasButtonOverride ? null : buttonTheme}
                        extend={ctaButtonStyles(heading || subheading)}
                        slug={link.slug}
                        className={link_class}
                    ></WordpressLink>
                )}
            </ContentMaxWidth>
        </Container>
    );
}

const ctaButtonStyles = hasContentAbove => ({
    display: "block",
    extend: [
        {
            condition: hasContentAbove,
            style: {
                marginTop: 20
            }
        }
    ]
});

const Heading = createComponent(
    ({ type }) => ({
        extend: [
            {
                condition: type == "data-banner",
                style: {
                    ...textStyles.heading3,
                    margin: 0
                }
            },
            {
                condition: type == "cta",
                style: {
                    ...textStyles.heading3,
                    margin: 0
                }
            }
        ],
        maxWidthTablet: {
            marginBottom: 24
        }
    }),
    "h2",
    ["dangerouslySetInnerHTML"]
);

const Subheading = createComponent(
    ({ type }) => ({
        ...textStyles.paragraphLarge,
        margin: "15px 0 0 0",
        extend: [
            {
                condition: type == "data-banner",
                style: {
                    marginTop: 20
                }
            }
        ]
    }),
    "h3",
    ["dangerouslySetInnerHTML"]
);

const Container = createComponent(
    ({ backgroundColor }) => ({
        backgroundColor: backgroundColor,
        textAlign: "center",
        margin: "60px 30px",
        borderRadius: "30px",
        "> *": {
            position: "relative",
            zIndex: 1
        },
        extend: [
            fluid("paddingH", gutters.default),
            fluid("paddingV", { max: 65, min: 40 })
        ],
        maxWidthMobile: {
            extend: [fluid("paddingV", gutters.vertical)]
        }
    }),
    "div",
    ["id"]
);
